import { z } from 'zod';
import { APPOINTMENT_DEFAULT_DURATION, appointmentDurationSchema } from './appointment-duration.js';
import { inquiryAddressSchema } from '../inquiry/address.js';
import { inquiryListRowSchema } from '../inquiry/inquiry-list.js';
import { buildingTypeSchema } from '../inquiry/building-type.js';
import { inquiryStatusSchema } from '../inquiry/status.js';
import { followUpStepSchema } from './passage-notice.js';
import { dayOf } from './deadline.js';
import { MINUTES_PER_DAY } from './internal-meeting.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * Ce que l'écran « Planning » lit : **une fenêtre de temps, pas une page**.
 *
 * ⚠️ **Pas d'enveloppe paginée, contrairement aux autres lectures du bureau**
 * ([D24](../../../../docs/ARCHITECTURE.md#d24)), et ce n'est pas un oubli : une grille de semaine
 * affiche *tous* les créneaux de la fenêtre ou elle ment. Un « 50 premiers » y ferait disparaître
 * des rendez-vous d'une journée chargée sans que rien ne l'indique — l'équipe partirait sur un
 * planning amputé. Ce qui borne la lecture est la **fenêtre**, et elle est bornée elle-même
 * (`PLANNING_WINDOW_MAX_DAYS`) ; le filtrage reste fait en base, ce qui est la vraie exigence de
 * D24.
 */
/**
 * L'amplitude maximale d'une fenêtre, en jours. Cinq semaines : la grille en montre une, le mois
 * glissant en montre quatre, et une marge d'un jour absorbe les bornes inclusives.
 *
 * ⚠️ **Une borne haute, pas un refus** ([M21](../../../../docs/MODELE-DONNEES.md#m21)) : une
 * fenêtre plus large est **ramenée** à cette amplitude à la frontière HTTP, elle ne rend pas
 * `400`. Sans elle, `from=2000-01-01&to=2099-12-31` balaie la table entière — la lecture est
 * authentifiée, mais un signet mal recopié suffirait.
 */
export const PLANNING_WINDOW_MAX_DAYS = 35;
/**
 * Un rendez-vous tel que la grille l'affiche : le créneau, la demande qu'il sert, et l'équipe
 * qui s'y rend.
 *
 * ⚠️ **Pas de « type de contrôle »**, que le prototype colore pourtant sur chaque carte :
 * `Inquiry.purpose` a désormais sa colonne (M32), mais l'API ne la sert pas encore. Le déclarer
 * ici pour le rendre en `null` constant ferait croire à un câblage — même règle que
 * `inquiryListRowSchema` et `reminderRowSchema`.
 *
 * ⚠️ **Pas d'état propre au rendez-vous non plus.** Les quatre pastilles du prototype (« RDV
 * fait », « En contrôle », « À valider », « Validé ») sont des lectures d'`Inquiry.status`
 * ([M3](../../../../docs/MODELE-DONNEES.md#m3)) — `Appointment` ne porte aucun `status`, et c'est
 * délibéré. La ligne rend donc le statut de la demande, l'écran choisit le libellé.
 */
export const plannedAppointmentSchema = z.object({
    id: uuidV7Schema,
    /** La durée, en minutes (#431) — une heure pour un rendez-vous d'avant elle. */
    durationMinutes: appointmentDurationSchema.default(APPOINTMENT_DEFAULT_DURATION),
    /** Instant du créneau, fuseau compris — `appointmentSlot()` le met en forme (M5, M24). */
    scheduledAt: z.iso.datetime({ offset: true }),
    /** L'étape de la suite qui l'a imposé — RDV1 ou RDV2 (#574) —, `null` s'il a été convenu. */
    imposedStep: followUpStepSchema.nullable().default(null),
    inquiry: z.object({
        id: uuidV7Schema,
        reference: z.string().nullable(),
        caseReference: z.string().nullable(),
        status: inquiryStatusSchema,
        address: inquiryAddressSchema,
        /** L'avis encore ouvert : le champ de la ligne de « Demandes », pour que la carte dise pareil. */
        passageNoticedAt: inquiryListRowSchema.shape.passageNoticedAt,
        /** La dernière étape du contrôle (M42), le champ de la ligne de « Demandes ». */
        progress: inquiryListRowSchema.shape.progress,
        /** La personne à contacter (#447), comme sur la carte « À planifier ». */
        contact: inquiryListRowSchema.shape.contact,
        /** La note pour l'équipe (#433) — la carte la montre au survol, la boîte la rouvre. */
        comment: z.string().nullable().default(null),
        /** Le type de bâti du lieu (#586) : la carte le dit, pour préparer la visite. */
        buildingType: buildingTypeSchema.nullable().default(null),
    }),
    /**
     * L'équipe affectée à la demande, `null` quand personne ne l'est encore.
     *
     * ⚠️ **Un rendez-vous sans équipe est un cas réel, pas une anomalie** : le bureau pose le
     * créneau avec l'usager au téléphone, l'affectation vient après. La grille doit donc savoir
     * afficher une ligne « non affecté » plutôt que d'escamoter le créneau — sinon le planning
     * paraît vide un jour où deux usagers attendent.
     */
    team: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
    })
        .nullable(),
});
/**
 * Un point interne tel que la grille l'affiche. Même forme de créneau qu'un rendez-vous, et
 * l'équipe **n'y est pas nullable** : c'est elle qui définit un point interne (`internalMeeting`
 * n'existe que rattaché à une équipe au moins), là où un rendez-vous existe sans affectation.
 * Partagé par plusieurs équipes (#576), il les porte toutes, triées par nom : la grille le peint
 * sur la ligne de chacune.
 *
 * `durationMinutes` (M28, 22/09/2026) — le rendez-vous a la sienne depuis #431.
 *
 * ⚠️ **Le prototype validé la saisit et ne la montre pas** — sa carte de grille n'affiche que
 * l'heure de début (`buildCalGrid`, `mocks/ghp-bo/app.js`). La servir ici est donc un **écart
 * assumé, à faire arbitrer** : une durée qu'aucun écran ne relit serait une donnée sans lecteur,
 * et l'opérateur qui vient de la choisir n'aurait aucun moyen de vérifier ce qu'il a enregistré.
 * Ce que la carte en fait se décide côté back-office, pas ici.
 */
export const plannedInternalMeetingSchema = z.object({
    id: uuidV7Schema,
    scheduledAt: z.iso.datetime({ offset: true }),
    title: z.string(),
    place: z.string().nullable(),
    durationMinutes: z.int().positive(),
    teams: z
        .array(z.object({
        id: uuidV7Schema,
        name: z.string(),
    }))
        .min(1),
});
/**
 * La fenêtre servie : les deux natures de créneau, **séparées**.
 *
 * ⚠️ **Deux tableaux plutôt qu'un seul discriminé par un champ `kind`.** Les deux natures n'ont
 * pas les mêmes champs — l'une porte une demande et un statut, l'autre un intitulé et un lieu —
 * et une union discriminée obligerait chaque lecture à se brancher avant de savoir ce qu'elle
 * tient. Séparées, le typage suffit : la grille itère l'une puis l'autre et les trie ensemble sur
 * `scheduledAt`, qui est le seul champ qu'elles partagent vraiment.
 */
export const planningWeekSchema = z.object({
    appointments: z.array(plannedAppointmentSchema),
    internalMeetings: z.array(plannedInternalMeetingSchema),
    /**
     * Le premier rendez-vous en vigueur **après** la fenêtre, sous les mêmes filtres (#459) : une
     * semaine vide dit où reprendre — « prochain le 4 août → ». Absent ou `null` : rien ne suit.
     */
    nextAppointmentAt: z.iso.datetime({ offset: true }).nullish(),
});
/**
 * Les points internes de l'équipe du jeton, tels que la **tablette** les descend (#319) — les
 * créneaux « BUREAU » que GHP DIGIT réservait dans l'agenda de l'agent.
 *
 * ⚠️ **La fenêtre voyage avec les lignes**, et c'est ce qui permet à la tablette de remplacer
 * sans rien perdre : un point annulé ou déplacé n'est plus servi, mais il n'est **absent** que
 * sur les jours que la réponse couvre. Hors de `from → to`, le serveur ne s'est pas prononcé, et
 * ce que la tablette garde de la veille reste vrai. Recalculer la fenêtre côté tablette ferait
 * dépendre ce ménage de son horloge — une tablette mal réglée effacerait la semaine qu'elle
 * n'a pas demandée.
 *
 * Mêmes bornes que {@link planningWindowSchema}, en **jours de Paris** : `from` inclus, `to` exclu.
 */
export const tourInternalMeetingsSchema = z.object({
    /** Premier jour couvert, **inclus** — le lundi de la semaine courante. */
    from: z.iso.date(),
    /** Jour **exclu** — la fenêtre fait {@link PLANNING_WINDOW_MAX_DAYS} jours. */
    to: z.iso.date(),
    items: z.array(plannedInternalMeetingSchema),
});
/**
 * Ce que le serveur accepte pour construire une fenêtre — la **seule** définition dont dérivent
 * l'URL de l'écran, le `WHERE` du dépôt et l'appel du client typé (D24).
 *
 * ⚠️ **La fenêtre est semi-ouverte : `from` inclus, `to` exclu.** C'est ce qui évite le défaut
 * classique du « 23:59:59 » — une borne haute inclusive posée à minuit perd tout créneau de la
 * dernière journée, et posée à 23:59:59 perd celui de 23:59:59.5. L'appelant demande donc le
 * lundi suivant pour obtenir la semaine complète.
 *
 * ⚠️ Schéma **strict** : il décrit ce que le service reçoit, pas ce qu'une URL contient. La
 * tolérance M21 — une borne illisible retombe sur la semaine courante, une amplitude excessive
 * est ramenée — vit à la frontière HTTP (`apps/api/src/planning/planning-window-query.ts`), au
 * même endroit et de la même façon que pour le tableau des demandes.
 */
export const planningWindowSchema = z.object({
    /** Première borne, **incluse**. */
    from: z.date(),
    /** Seconde borne, **exclue**. */
    to: z.date(),
    /** `all`, ou l'équipe dont on ne veut que les créneaux — jamais son nom (M22). */
    teamId: z.union([z.literal('all'), uuidV7Schema]),
    /**
     * Adresse, commune ou référence : `''` ne filtre rien (#357).
     *
     * ⚠️ **Elle ne retient que les rendez-vous.** Un point interne n'a ni adresse ni référence ; le
     * masquer à chaque recherche ferait croire l'équipe libre sur un créneau qu'elle a pris.
     */
    search: z.string(),
});
/**
 * Le jour civil, décalé de `count` jours — **l'arithmétique de calendrier de la grille**.
 *
 * ⚠️ Le calcul passe par **midi** UTC : minuit basculerait d'un jour au moindre décalage de fuseau
 * ou changement d'heure, et la semaine s'ouvrirait un dimanche deux fois par an.
 *
 * ⚠️ `deadline.ts` porte une fonction identique, restée privée, et ce n'est pas un oubli à
 * fusionner dans la foulée : celle-là sert une arithmétique d'**échéance réglementaire**, dont les
 * tests épinglent des jours précis. Les réunir est un remaniement à part, pas un effet de bord d'un
 * écran.
 */
export function shiftDays(day, count) {
    const moved = new Date(`${day}T12:00:00.000Z`);
    moved.setUTCDate(moved.getUTCDate() + count);
    return moved.toISOString().slice(0, 'AAAA-MM-JJ'.length);
}
/**
 * Les jours de Paris qu'un point interne occupe (#576) — la grille et l'agenda le peignent sur
 * chacun. Une durée de quelques heures tient sur son jour ; au-delà, chaque **journée entamée**
 * compte, à partir du jour du début : trois jours posés un lundi à 8 h prennent lundi, mardi et
 * mercredi, et non le jeudi où tombe l'instant de fin.
 */
export function internalMeetingDays(scheduledAt, durationMinutes) {
    const start = new Date(scheduledAt);
    if (Number.isNaN(start.getTime()))
        return [];
    const first = dayOf(start);
    const count = Math.max(1, Math.ceil(durationMinutes / MINUTES_PER_DAY));
    return Array.from({ length: count }, (_, index) => shiftDays(first, index));
}
/**
 * Le lundi de la semaine d'un jour civil — l'origine de la grille, qui commence au lundi comme le
 * prototype validé.
 *
 * ⚠️ **Dans le domaine et non dans chaque appelant** : le serveur s'en sert pour la fenêtre qu'il
 * sert par défaut, l'écran pour celle qu'il affiche. Deux copies finiraient par ouvrir deux
 * semaines différentes du même jour, et personne ne verrait laquelle est juste.
 */
export function mondayOfWeek(day) {
    // `getUTCDay()` rend 0 pour dimanche : `(weekday + 6) % 7` ramène lundi à 0 et dimanche à 6.
    const sinceMonday = (new Date(`${day}T12:00:00.000Z`).getUTCDay() + 6) % 7;
    return shiftDays(day, -sinceMonday);
}
//# sourceMappingURL=planning-week.js.map